import { Badge, Box, Flex, Group, Stack, Text, Tooltip } from "@mantine/core";
import { ReactNode } from "react";
import { useTranslation } from "react-i18next";
import { STATUS_COLOR } from "@constants/resultColors";
import { Comitance, HeringFlag, MotilityAnalysis } from "types/testTypes";
import {
  findPosition,
  formatPrismDioptres,
  MOTILITY_TR,
} from "../../result/utils/motilityPresentation";
import { useBadgeVariant } from "@hooks/useBadgeVariant";
import { BlockCaption, InfoHint } from "./MotilitySectionHeading";

const HERING_STATUS_COLOR: Record<HeringFlag["status"], string> = {
  confirmed: STATUS_COLOR.good,
  noSecondaryOveraction: STATUS_COLOR.cautionYellow,
  yokeUnmeasured: STATUS_COLOR.unavailable,
};

const HERING_STATUS_ORDER: HeringFlag["status"][] = [
  "confirmed",
  "noSecondaryOveraction",
  "yokeUnmeasured",
];

interface Finding {
  key: string;
  label: string;
  hint?: string;
  value: ReactNode;
}

interface FindingRowProps {
  finding: Finding;
}

function FindingRow({ finding }: FindingRowProps) {
  return (
    <Group justify="space-between" align="center" gap="md" wrap="nowrap">
      <Group gap={4} wrap="nowrap">
        <Text fz="xs" fw={600} tt="uppercase" c="dimmed">
          {finding.label}
        </Text>
        {finding.hint && <InfoHint label={finding.hint} />}
      </Group>
      {finding.value}
    </Group>
  );
}

/** A null verdict gets its own state, so missing evidence never wears the reassuring green. */
interface ComitanceValueProps {
  comitance: Comitance | null;
}

function ComitanceValue({ comitance }: ComitanceValueProps) {
  const { t } = useTranslation();
  const variant = useBadgeVariant();

  if (!comitance) {
    return (
      <Badge variant={variant} color={STATUS_COLOR.unavailable}>
        {t(`${MOTILITY_TR}.comitance.unavailable`)}
      </Badge>
    );
  }

  // Both gates decide the verdict, and the range gate binds first for most
  // spreads, so showing only the spread leaves an "Incomitant" badge unexplained.
  const gates = [
    comitance.deviationStdDevPrismDioptres != null
      ? t(`${MOTILITY_TR}.comitance.spreadShort`, {
          value: comitance.deviationStdDevPrismDioptres.toFixed(1),
          threshold: comitance.thresholdPrismDioptres.toFixed(1),
        })
      : null,
    comitance.deviationRangePrismDioptres != null
      ? t(`${MOTILITY_TR}.comitance.rangeShort`, {
          value: comitance.deviationRangePrismDioptres.toFixed(1),
          threshold: comitance.rangeThresholdPrismDioptres.toFixed(1),
        })
      : null,
  ].filter(Boolean);

  return (
    <Group gap={6} wrap="nowrap">
      {gates.length > 0 && (
        <Text fz="xs" c="dimmed">
          {gates.join(" · ")}
        </Text>
      )}
      <Badge
        variant={variant}
        color={
          comitance.isComitant === null
            ? STATUS_COLOR.unavailable
            : comitance.isComitant
              ? STATUS_COLOR.good
              : STATUS_COLOR.cautionOrange
        }
      >
        {comitance.isComitant === null
          ? t(`${MOTILITY_TR}.comitance.indeterminate`, { count: comitance.positionsMeasured })
          : t(`${MOTILITY_TR}.comitance.${comitance.isComitant ? "comitant" : "incomitant"}`)}
      </Badge>
    </Group>
  );
}

interface YokeValueProps {
  flags: HeringFlag[];
}

function YokeValue({ flags }: YokeValueProps) {
  const { t } = useTranslation();
  const variant = useBadgeVariant();

  if (flags.length === 0) {
    return (
      <Badge variant={variant} color={STATUS_COLOR.unavailable}>
        {t(`${MOTILITY_TR}.hering.noneShort`)}
      </Badge>
    );
  }

  return (
    <Group gap={4} justify="flex-end" wrap="wrap">
      {HERING_STATUS_ORDER.map((status) => {
        const count = flags.filter((flag) => flag.status === status).length;
        if (count === 0) return null;
        return (
          <Badge key={status} variant={variant} color={HERING_STATUS_COLOR[status]}>
            {count} {t(`${MOTILITY_TR}.hering.statusesShort.${status}`)}
          </Badge>
        );
      })}
    </Group>
  );
}

interface MotilityFindingsListProps {
  analysis: MotilityAnalysis;
}

/** The grid answers "which muscle"; these answer "and overall". */
export function MotilityFindingsList({ analysis }: MotilityFindingsListProps) {
  const { t } = useTranslation();
  const variant = useBadgeVariant();
  const shoots = analysis.verticalShoots;
  const comitance = analysis.comitance;

  const strong = (value: ReactNode) => (
    <Text fz="sm" fw={700} ta="right">
      {value}
    </Text>
  );

  const findings: Finding[] = [
    {
      key: "primaryDeviation",
      label: t(`${MOTILITY_TR}.deviation.primaryLabel`),
      hint: t(`${MOTILITY_TR}.deviation.helper`),
      value: strong(formatPrismDioptres(findPosition(analysis, "primary")?.deviationPrismDioptres)),
    },
    {
      key: "comitance",
      label: t(`${MOTILITY_TR}.comitance.label`),
      hint: t(`${MOTILITY_TR}.comitance.helper`),
      value: <ComitanceValue comitance={comitance} />,
    },
    {
      key: "yoke",
      label: t(`${MOTILITY_TR}.hering.summaryLabel`),
      hint: t(`${MOTILITY_TR}.hering.helper`),
      value: <YokeValue flags={analysis.heringFlags} />,
    },
    {
      key: "shoots",
      label: t(`${MOTILITY_TR}.shoots.summaryLabel`),
      hint: t(`${MOTILITY_TR}.shoots.helper`),
      value: (
        <Badge
          variant={variant}
          color={shoots.length > 0 ? STATUS_COLOR.cautionOrange : STATUS_COLOR.unavailable}
        >
          {shoots.length > 0
            ? t(`${MOTILITY_TR}.shoots.countLabel`, { count: shoots.length })
            : t(`${MOTILITY_TR}.shoots.noneShort`)}
        </Badge>
      ),
    },
  ];

  // The rows share the grid's height beside it, so each takes an equal slice.
  return (
    <Stack gap={6} w="100%" h="100%">
      <BlockCaption title={t(`${MOTILITY_TR}.findingsTitle`)} />
      <Flex direction="column" rowGap={4} style={{ flex: 1, minHeight: 0 }}>
        {findings.map((finding) => (
          <Flex
            key={finding.key}
            align="center"
            pb={3}
            style={{
              flex: "1 1 0",
              borderBottom: "1px solid var(--mantine-color-gray-3)",
            }}
          >
            <Box w="100%">
              <FindingRow finding={finding} />
            </Box>
          </Flex>
        ))}
      </Flex>
    </Stack>
  );
}

/** Data-quality caveats as badges, each carrying its full explanation. */
interface MotilityWarningsProps {
  warnings: MotilityAnalysis["warnings"];
}

export function MotilityWarnings({ warnings }: MotilityWarningsProps) {
  const { t } = useTranslation();
  const variant = useBadgeVariant();
  if (warnings.length === 0) return null;

  return (
    <Group gap={6} wrap="wrap">
      {warnings.map((warning) => (
        <Tooltip
          key={warning}
          label={t(`${MOTILITY_TR}.warnings.${warning}`)}
          withArrow
          multiline
          w={280}
        >
          <Badge variant={variant} color={STATUS_COLOR.cautionYellow} style={{ cursor: "help" }}>
            {t(`${MOTILITY_TR}.warnings.labels.${warning}`)}
          </Badge>
        </Tooltip>
      ))}
    </Group>
  );
}
